/** Sample a generated still into an element map. No physics — classify and light-paint. */

export const ELEMENT_KINDS = [
  "fire",
  "water",
  "earth",
  "wind",
  "steam",
  "cloud",
  "void",
] as const;

export type ElementKind = (typeof ELEMENT_KINDS)[number];

export const MOTION_PATHS = [
  "rise",
  "fall",
  "stream",
  "drift",
  "shear",
  "settle",
  "none",
] as const;

export type MotionPath = (typeof MOTION_PATHS)[number];

export type SceneSample = {
  cols: number;
  rows: number;
  cells: string[];
  kinds: ElementKind[];
  density: number[];
  edge: boolean[];
  paths: MotionPath[];
  deep: string;
  accent: string;
  glow: string;
};

function hex(r: number, g: number, b: number): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n)))
      .toString(16)
      .padStart(2, "0");
  return `#${c(r)}${c(g)}${c(b)}`;
}

function parseHex(h: string): [number, number, number] {
  const s = h.replace("#", "");
  if (s.length !== 6) return [20, 12, 28];
  return [
    parseInt(s.slice(0, 2), 16),
    parseInt(s.slice(2, 4), 16),
    parseInt(s.slice(4, 6), 16),
  ];
}

function sat(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  return max === 0 ? 0 : (max - min) / max;
}

function clamp01(n: number): number {
  return Math.max(0, Math.min(1, n));
}

export function classifyRgb(r: number, g: number, b: number): {
  kind: ElementKind;
  density: number;
} {
  const luma = (r * 0.3 + g * 0.59 + b * 0.11) / 255;
  const s = sat(r, g, b);
  if (r > 130 && r > b + 28 && r >= g - 12) {
    return { kind: "fire", density: clamp01((r / 255) * 0.55 + s * 0.45) };
  }
  if (b > 70 && b >= g - 18 && b > r + 6) {
    return { kind: "water", density: clamp01(b / 255 * 0.65 + (1 - s) * 0.25) };
  }
  if (luma > 0.64 && s < 0.2) {
    return { kind: "steam", density: clamp01((luma - 0.5) * 2) };
  }
  if (luma > 0.46 && s < 0.18 && b >= r - 4) {
    return { kind: "cloud", density: clamp01(luma) };
  }
  if (luma > 0.56 && s < 0.3) {
    return { kind: "wind", density: clamp01(0.28 + luma * 0.45) };
  }
  if (luma < 0.13) {
    return { kind: "void", density: clamp01(1 - luma) };
  }
  return { kind: "earth", density: clamp01(0.32 + (1 - s) * 0.35) };
}

export function pathForKind(kind: ElementKind): MotionPath {
  if (kind === "fire" || kind === "steam") return "rise";
  if (kind === "water") return "fall";
  if (kind === "cloud") return "drift";
  if (kind === "wind") return "shear";
  if (kind === "earth") return "settle";
  return "none";
}

export function markEdges(kinds: ElementKind[], cols: number, rows: number): boolean[] {
  const edge = kinds.map(() => false);
  const at = (x: number, y: number) => kinds[y * cols + x];
  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const k = at(x, y);
      const n =
        (x > 0 && at(x - 1, y) !== k) ||
        (x + 1 < cols && at(x + 1, y) !== k) ||
        (y > 0 && at(x, y - 1) !== k) ||
        (y + 1 < rows && at(x, y + 1) !== k);
      edge[y * cols + x] = Boolean(n);
    }
  }
  return edge;
}

export function enrichScene(raw: Pick<SceneSample, "cols" | "rows" | "cells"> & Partial<SceneSample>): SceneSample {
  const cols = raw.cols;
  const rows = raw.rows;
  const cells = raw.cells;
  const kinds =
    raw.kinds?.length === cells.length
      ? raw.kinds
      : cells.map((c) => {
          const [r, g, b] = parseHex(c);
          return classifyRgb(r, g, b).kind;
        });
  const density =
    raw.density?.length === cells.length
      ? raw.density
      : cells.map((c, i) => {
          const [r, g, b] = parseHex(c);
          return classifyRgb(r, g, b).density || (kinds[i] === "void" ? 0.2 : 0.45);
        });
  const edge =
    raw.edge?.length === cells.length ? raw.edge : markEdges(kinds, cols, rows);
  const paths =
    raw.paths?.length === cells.length
      ? raw.paths
      : kinds.map((k, i) => (edge[i] && k !== "void" ? "stream" : pathForKind(k)));
  return {
    cols,
    rows,
    cells,
    kinds,
    density,
    edge,
    paths,
    deep: raw.deep || cells[0] || "#12081a",
    accent: raw.accent || cells[Math.floor(cells.length / 2)] || "#a78bfa",
    glow: raw.glow || cells[cells.length - 1] || "#f97316",
  };
}

export function sampleScene(
  pixels: Uint8ClampedArray,
  width: number,
  height: number,
  cols = 12,
  rows = 7,
): SceneSample {
  const cw = Math.max(1, cols);
  const rh = Math.max(1, rows);
  const cells: string[] = [];
  const kinds: ElementKind[] = [];
  const density: number[] = [];
  let dark: [number, number, number, number] = [255, 255, 255, 999];
  let bright: [number, number, number, number] = [0, 0, 0, -1];
  let punch: [number, number, number, number] = [0, 0, 0, -1];

  for (let y = 0; y < rh; y++) {
    for (let x = 0; x < cw; x++) {
      const x0 = Math.floor((x / cw) * width);
      const x1 = Math.floor(((x + 1) / cw) * width);
      const y0 = Math.floor((y / rh) * height);
      const y1 = Math.floor(((y + 1) / rh) * height);
      let r = 0;
      let g = 0;
      let b = 0;
      let n = 0;
      for (let py = y0; py < y1; py++) {
        for (let px = x0; px < x1; px++) {
          const i = (py * width + px) * 4;
          const a = pixels[i + 3] ?? 0;
          if (a < 16) continue;
          r += pixels[i] ?? 0;
          g += pixels[i + 1] ?? 0;
          b += pixels[i + 2] ?? 0;
          n += 1;
        }
      }
      if (!n) {
        cells.push("#12081a");
        kinds.push("void");
        density.push(0.2);
        continue;
      }
      r /= n;
      g /= n;
      b /= n;
      cells.push(hex(r, g, b));
      const cls = classifyRgb(r, g, b);
      kinds.push(cls.kind);
      density.push(cls.density);
      const luma = r * 0.3 + g * 0.59 + b * 0.11;
      if (luma < dark[3]) dark = [r, g, b, luma];
      if (luma > bright[3]) bright = [r, g, b, luma];
      const s = sat(r, g, b) * luma;
      if (s > punch[3]) punch = [r, g, b, s];
    }
  }

  return enrichScene({
    cols: cw,
    rows: rh,
    cells,
    kinds,
    density,
    deep: hex(dark[0] * 0.45, dark[1] * 0.45, dark[2] * 0.5),
    accent: hex(punch[0], punch[1], punch[2]),
    glow: hex(bright[0], bright[1], bright[2]),
  });
}

export function parseSceneSample(raw: unknown): SceneSample | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const o = raw as Record<string, unknown>;
  const cols = Math.max(2, Math.min(16, Number(o.cols) || 0));
  const rows = Math.max(2, Math.min(12, Number(o.rows) || 0));
  if (!Array.isArray(o.cells) || !cols || !rows) return undefined;
  const cells = o.cells
    .map((c) => String(c || ""))
    .filter((c) => /^#[0-9a-fA-F]{6}$/.test(c))
    .slice(0, cols * rows);
  if (cells.length < cols * rows) return undefined;
  const hx = (v: unknown, fb: string) =>
    typeof v === "string" && /^#[0-9a-fA-F]{6}$/.test(v) ? v : fb;
  const kinds = Array.isArray(o.kinds)
    ? o.kinds.map((k) => {
        const s = String(k);
        return (ELEMENT_KINDS as readonly string[]).includes(s) ? (s as ElementKind) : "earth";
      })
    : undefined;
  const density = Array.isArray(o.density)
    ? o.density.map((d) => clamp01(Number(d) || 0))
    : undefined;
  const edge = Array.isArray(o.edge) ? o.edge.map((e) => e === true) : undefined;
  const paths = Array.isArray(o.paths)
    ? o.paths.map((p) => {
        const s = String(p);
        return (MOTION_PATHS as readonly string[]).includes(s) ? (s as MotionPath) : "drift";
      })
    : undefined;
  return enrichScene({
    cols,
    rows,
    cells,
    kinds,
    density,
    edge,
    paths,
    deep: hx(o.deep, cells[0] || "#12081a"),
    accent: hx(o.accent, cells[Math.floor(cells.length / 2)] || "#a78bfa"),
    glow: hx(o.glow, cells[cells.length - 1] || "#f97316"),
  });
}

export function cellCenter(i: number, cols: number, rows: number): { x: number; y: number } {
  const c = Math.max(1, cols);
  const r = Math.max(1, rows);
  return {
    x: (((i % c) + 0.5) / c) * 100,
    y: ((Math.floor(i / c) + 0.5) / r) * 100,
  };
}

export function coerceKind(raw: unknown, fallback: ElementKind = "earth"): ElementKind {
  const t = String(raw || "").toLowerCase();
  if ((ELEMENT_KINDS as readonly string[]).includes(t)) return t as ElementKind;
  if (/leaf|wood|tree|forest|dirt|rock|sand|petal|moss/.test(t)) return "earth";
  if (/lava|fire|flame|ember|magma/.test(t)) return "fire";
  if (/rain|ocean|river|sea|wave|kelp/.test(t)) return "water";
  if (/smoke|fog|mist|vapor/.test(t)) return "steam";
  if (/cloud|sky/.test(t)) return "cloud";
  if (/wind|gale|breeze/.test(t)) return "wind";
  return fallback;
}

export function coercePath(raw: unknown, fallback: MotionPath = "drift"): MotionPath {
  const t = String(raw || "").toLowerCase();
  if ((MOTION_PATHS as readonly string[]).includes(t)) return t as MotionPath;
  if (/waterfall|fall|drop|rain|leaf|snow/.test(t)) return "fall";
  if (/rise|up|ember|smoke/.test(t)) return "rise";
  if (/stream|flow|river/.test(t)) return "stream";
  if (/shear|wind|blow/.test(t)) return "shear";
  if (/settle|rest|still/.test(t)) return "settle";
  if (/drift|float|cloud/.test(t)) return "drift";
  return fallback;
}

export function inferMapFromBrief(brief: string): {
  object: ElementKind;
  path: MotionPath;
  density: number;
} {
  const t = (brief || "").toLowerCase();
  if (/waterfall/.test(t)) return { object: "water", path: "fall", density: 0.72 };
  if (/leaf|leaves|petal/.test(t)) return { object: "earth", path: "fall", density: 0.66 };
  if (/\bsnow\b|ash\b/.test(t)) return { object: "steam", path: "fall", density: 0.6 };
  if (/fire|lava|volcano|flame|ember|magma/.test(t)) return { object: "fire", path: "rise", density: 0.7 };
  if (/steam|smoke|mist|fog/.test(t)) return { object: "steam", path: "rise", density: 0.55 };
  if (/cloud/.test(t)) return { object: "cloud", path: "drift", density: 0.5 };
  if (/wind|gale|breeze/.test(t)) return { object: "wind", path: "shear", density: 0.55 };
  if (/water|ocean|river|wave|kelp|rain/.test(t)) {
    return { object: "water", path: /kelp|drift/.test(t) ? "drift" : /rain/.test(t) ? "fall" : "stream", density: 0.6 };
  }
  if (/earth|forest|wood|rock|mountain/.test(t)) return { object: "earth", path: "settle", density: 0.42 };
  return { object: "earth", path: "drift", density: 0.45 };
}

export function stampPlanOnScene(
  scene: SceneSample,
  object: ElementKind,
  path: MotionPath,
  density: number,
): SceneSample {
  const kinds = scene.kinds.slice();
  const paths = scene.paths.slice();
  const dens = scene.density.slice();
  const edge = scene.edge.slice();
  const n = kinds.length;
  if (!n) return scene;
  const take = Math.max(6, Math.round(n * Math.max(0.28, Math.min(1, density))));
  for (let i = 0; i < take; i++) {
    const idx = (i * 7 + 3) % n;
    kinds[idx] = object;
    paths[idx] = path;
    dens[idx] = Math.max(dens[idx] ?? 0, density);
    edge[idx] = true;
  }
  return { ...scene, kinds, paths, density: dens, edge };
}

export type SceneObject = {
  key: string;
  kind: ElementKind;
  path: MotionPath;
  x: number;
  y: number;
  delay: string;
  den: number;
};

export function objectsFromPlan(
  object: ElementKind,
  path: MotionPath,
  density: number,
): SceneObject[] {
  const den = clamp01(density);
  const n = Math.max(16, Math.min(52, Math.round(14 + den * 42)));
  const out: SceneObject[] = [];
  for (let i = 0; i < n; i++) {
    const seed = (i + 1) * 47 + Math.round(den * 13);
    out.push({
      key: `obj-${i}`,
      kind: object,
      path,
      x: (seed * 13) % 94 + 3,
      y: (seed * 29) % 88 + 4,
      delay: `${((i * 0.27) % 4.2).toFixed(2)}s`,
      den,
    });
  }
  return out;
}
